import React, { useState, useEffect } from "react";
import { Search } from "lucide-react";
import "./NewUserModal.css";
import LocationDropdowns from "../../components/LocationDropdowns";
import Permiso from "../../router/Permiso";
import { DocumentServices } from "../../services/DocumentService";
import { RegionService } from "../../services/RegionService";
import { PerfilService } from "../../services/PerfilService";
import { UsuarioService } from "../../services/UsuarioService";
import { ClienteService } from "../../services/ClienteService";
import { toast } from "react-toastify";

const initialState = {
  tipoDocumento: "",
  numero: "",
  perfil: "",
  nombre: "",
  apellidoPaterno: "",
  apellidoMaterno: "",
  celular: "",
  email: "",
  fechaNacimiento: "",
  departamento: "",
  provincia: "",
  distrito: "",
  direccion: "",
  usuario: "",
  // Contenedor 3
  id_region: "",
  jefe: "",
  supervisor: "",
};

const perfilesEquipoTrabajo = [
  "Vendedor",
  "Supervisor",
  "Callcenter",
  "Asesor",
];

function NewUserModal({
  open,
  onClose,
  onSave,
  initialData = {},
  readOnly = false,
  token,
}) {
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [regionesDisponibles, setRegionesDisponibles] = useState([]);
  const [loadingRegiones, setLoadingRegiones] = useState(false);
  const [jefesDisponibles, setJefesDisponibles] = useState([]);
  const [supervisoresDisponibles, setSupervisoresDisponibles] = useState([]);
  const [loadingJefes, setLoadingJefes] = useState(false);
  const [loadingSupervisores, setLoadingSupervisores] = useState(false);
  const [tiposDocumentos, setTiposDocumentos] = useState([]);
  const [perfiles, setPerfiles] = useState([]);

  // Estado para las ubicaciones
  const [locationData, setLocationData] = useState({
    department: "",
    province: "",
    district: "",
    zone: "",
  });

  const tipoSeleccionado = perfiles.find((td) => td.id == form.perfil);

  // Estado para controlar el modo de edición
  const [isEditing, setIsEditing] = useState(false);

  // Función para validar que solo se ingresen números
  const validateNumericInput = (value) => {
    return value.replace(/[^0-9]/g, "");
  };

  // Función para manejar cambios en campos numéricos
  const handleNumericChange = (e) => {
    const { name, value } = e.target;
    const numericValue = validateNumericInput(value);
    setForm((prev) => ({
      ...prev,
      [name]: numericValue,
    }));
  };

  // Función para cargar las regiones disponibles
  const cargarRegiones = async () => {
    setLoadingRegiones(true);
    try {
      const data = await RegionService.fetchRegiones(token);
      setRegionesDisponibles(data);
    } catch (error) {
      console.error("Error al cargar regiones:", error);
    } finally {
      setLoadingRegiones(false);
    }
  };

  const cargarTipoDocumentos = async () => {
    setLoadingRegiones(true);
    try {
      const tipos = await DocumentServices.getDocuments(token);
      setTiposDocumentos(tipos || []);
    } catch (error) {
      console.error("Error al cargar tipos de Documentos:", error);
    } finally {
      setLoadingRegiones(false);
    }
  };

  const cargarPerfiles = async () => {
    setLoadingRegiones(true);
    try {
      const data = await PerfilService.fetchPerfiles(token);
      setPerfiles(data);
    } catch (error) {
      console.error("Error al cargar perfiles:", error);
    } finally {
      setLoadingRegiones(false);
    }
  };

  // Función para cargar jefes disponibles
  const cargarJefes = async () => {
    setLoadingJefes(true);
    try {
      const data = await UsuarioService.fetchJefes(token);
      setJefesDisponibles(data);
    } catch (error) {
      setJefesDisponibles([]);
       console.log(error);
    } finally {
      setLoadingJefes(false);
    }
  };

  // Función para cargar supervisores disponibles
  const cargarSupervisores = async () => {
    setLoadingSupervisores(true);
    try {
      const data = await UsuarioService.fetchSupervisores(token);
      setSupervisoresDisponibles(data);
    } catch (error) {
      setSupervisoresDisponibles([]);
      console.log(error);
    } finally {
      setLoadingSupervisores(false);
    }
  };

  // Función para cambiar al modo de edición
  const handleEditMode = () => {
    setIsEditing(true);
  };

  // Función para cancelar la edición y volver al modo consulta
  const handleCancelEdit = () => {
    setIsEditing(false);
    // Restaurar los datos originales
    if (initialData && Object.keys(initialData).length > 0) {
      const mappedData = {
        tipoDocumento: initialData.tipoDocumento || "",
        numero: initialData.numero || "",
        perfil: initialData.perfil || "",
        nombre: initialData.nombre || "",
        apellidoPaterno: initialData.apellidoPaterno || "",
        apellidoMaterno: initialData.apellidoMaterno || "",
        celular: initialData.celular || "",
        email: initialData.email || "",
        fechaNacimiento: initialData.fechaNacimiento || "",
        departamento: initialData.departamento || "",
        provincia: initialData.provincia || "",
        distrito: initialData.distrito || "",
        direccion: initialData.direccion || "",
        id_region: initialData.id_region || "",
        jefe: initialData.jefe || "",
        supervisor: initialData.supervisor || "",
        usuario: initialData.usuario || "",
        password: "",
      };
      setForm(mappedData);
    }
  };

  // Función para manejar el cambio de perfil
  const handlePerfilChange = (e) => {
    const nuevoPerfil = e.target.value;
    setForm((prev) => ({
      ...prev,
      perfil: nuevoPerfil,
      // Limpiar jefe y supervisor cuando cambia el perfil
      jefe: "",
      supervisor: "",
    }));

    // Si el perfil es Jefe, cargar supervisores
    if (nuevoPerfil === "Jefe") {
      cargarSupervisores();
    }
    // Si el perfil es Supervisor, cargar jefes
    else if (nuevoPerfil === "Supervisor") {
      cargarJefes();
    }
    // Si el perfil es Vendedor, Callcenter o Asesor, cargar ambos
    else if (["Vendedor", "Callcenter", "Asesor"].includes(nuevoPerfil)) {
      cargarJefes();
      cargarSupervisores();
    }
  };

  const buscarCliente = async () => {
    try {
      if (!form.numero || form.numero.trim() === "") {
        setErrors((prev) => ({
          ...prev,
          numero: "Ingrese un número válido",
        }));
        return;
      }

      const cliente = await ClienteService.buscarPorDNI({ token, numero: form.numero });

      if (cliente) {
        setForm((prev) => ({
          ...prev,
          nombre: cliente.nombres,
          apellidoMaterno: cliente.apellido_materno,
          apellidoPaterno: cliente.apellido_paterno,
        }));

        setErrors((prev) => ({
          ...prev,
          numero: null,
        }));
      }
    } catch (err) {
      const mensaje = err.response?.data?.error || err.message || "Error al consultar el servicio";
      setErrors((prev) => ({
        ...prev,
        numero: mensaje,
      }));
    }
  };

  useEffect(() => {
    if (open) {
      // Cargar regiones cuando se abre el modal
      cargarRegiones();

      cargarTipoDocumentos();

      cargarPerfiles();

      // Cargar jefes y supervisores por defecto
      cargarJefes();
      cargarSupervisores();

      // Mapear los datos del usuario al formato que espera el formulario
      const mappedData =
        initialData && Object.keys(initialData).length > 0
          ? {
            ...initialState,
            // Mapear campos de la tabla a campos del formulario
            tipoDocumento:
              initialData.tipo_documento || initialData.tipoDocumento || "",
            numero:
              initialData.numero_documento ||
              initialData.numero ||
              initialData.documento ||
              "",
            perfil: initialData.perfil || "",
            nombre: initialData.nombres || initialData.nombre || "",
            apellidoPaterno: initialData.apellidoPaterno || "",
            apellidoMaterno: initialData.apellidoMaterno || "",
            celular: initialData.celular || "",
            email: initialData.email || "",
            fechaNacimiento: initialData.fechaNacimiento || "",
            departamento: initialData.departamento || "",
            provincia: initialData.provincia || "",
            distrito: initialData.distrito || "",
            direccion: initialData.direccion || "",
            personaContacto:
              initialData.personaContacto || initialData.referencia || "",
            telefonoEmergencia:
              initialData.telefonoEmergencia || initialData.telefono1 || "",
            personaContacto2: initialData.personaContacto2 || "",
            telefonoEmergencia2:
              initialData.telefonoEmergencia2 || initialData.telefono2 || "",
            talla: initialData.talla || "",
            tieneHijos: initialData.tieneHijos || "",
            nivelEducacion: initialData.nivelEducacion || "",
            enfermedades: initialData.enfermedades || [],
            id_region: initialData.id_region || "",
            jefe: initialData.jefe || "",
            supervisor: initialData.supervisor || "",
            password: initialData.password || "",
            usuario: initialData.usuario || "",
            enfermedadInput: "",
          }
          : initialState;

      setForm(mappedData);

      // Inicializar locationData si no está en modo readOnly
      if (!readOnly) {
        setLocationData({
          department: "",
          province: "",
          district: "",
          zone: "",
        });
      } else if (readOnly) {
        setLocationData({
          department: initialData.departamento,
          province: initialData.provincia,
          district: initialData.distrito,
          zone: "",
        });
      }

      setTab(0);
      setErrors({});
      setSuccess("");
    }
  }, [open, initialData]);

  if (!open) return null;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Funciones para manejar cambios de ubicación
  const handleDepartmentChange = (value) => {
    setLocationData((prev) => ({
      ...prev,
      department: value,
      province: "",
      district: "",
      zone: "",
    }));
    // Sincronizar con form
    setForm((prev) => ({
      ...prev,
      departamento: value,
      provincia: "",
      distrito: "",
      region: "",
    }));
  };

  const handleProvinceChange = (value) => {
    setLocationData((prev) => ({
      ...prev,
      province: value,
      district: "",
      zone: "",
    }));
    // Sincronizar con form
    setForm((prev) => ({
      ...prev,
      provincia: value,
      distrito: "",
      region: "",
    }));
  };

  const handleDistrictChange = (value) => {
    setLocationData((prev) => ({
      ...prev,
      district: value,
      zone: "",
    }));
    // Sincronizar con form
    setForm((prev) => ({
      ...prev,
      distrito: value,
      region: "",
    }));
  };

  const handleZoneChange = (value) => {
    setLocationData((prev) => ({
      ...prev,
      zone: value,
    }));
    // Sincronizar con form
    setForm((prev) => ({
      ...prev,
      region: value,
    }));
  };

  const validate = () => {
    let errs = {};
    // Contenedor 1
    if (!form.tipoDocumento) errs.tipoDocumento = "Requerido";
    if (!form.numero) errs.numero = "Requerido";
    if (!form.perfil) errs.perfil = "Requerido";
    if (!form.nombre) errs.nombre = "Requerido";
    if (!form.email) errs.email = "Requerido";

    // Validar región siempre
    if (!form.id_region) errs.region = "Requerido";

    // Validar jefe y supervisor según el perfil
    // En modo edición, ser más flexibles con la validación
    if (perfilesEquipoTrabajo.includes(tipoSeleccionado?.nombre)) {
      if (tipoSeleccionado?.nombre === "Jefe") {
        // Para jefes, solo validar supervisor
        //if (!form.supervisor) errs.supervisor = "Requerido";
      } else if (tipoSeleccionado?.nombre === "Supervisor") {
        // Para supervisores, solo validar jefe
        if (!form.jefe) errs.jefe = "Requerido";
      } else {
        // Para vendedores, callcenter y asesores
        // En modo edición, permitir que estos campos estén vacíos
        if (!readOnly || !isEditing) {
          if (!form.jefe) errs.jefe = "Requerido";
          if (!form.supervisor) errs.supervisor = "Requerido";
        }
      }
    }
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    setErrors(errs);

    if (Object.keys(errs).length === 0) {
      setLoading(true);
      try {
        const userData = {
          tipoDocumento: form.tipoDocumento,
          documento: form.numero,
          perfil: form.perfil,
          nombre: form.nombre,
          apellidoPaterno: form.apellidoPaterno,
          apellidoMaterno: form.apellidoMaterno,
          celular: form.celular,
          email: form.email,
          password: form.password,
          fechaNacimiento: form.fechaNacimiento,
          departamento: form.departamento,
          provincia: form.provincia,
          distrito: form.distrito,
          direccion: form.direccion,
          id_region: form.id_region,
          jefe: form.jefe,
          supervisor: form.supervisor,
          usuario: form.usuario,
          telefono1: form.telefonoEmergencia,
          telefono2: form.telefonoEmergencia2,
          referencia: form.personaContacto,
          modeloDispositivo: "",
          imeiDispositivo: "",
          codigoReferido: "",
        };

        const isEdicion = readOnly && isEditing && initialData?.id;
        const idUsuario = initialData?.id || null;

        const savedUser = await UsuarioService.guardarUsuario({
          token,
          userData,
          isEdicion,
          idUsuario,
        });


        toast.success(isEdicion ? "Usuario actualizado correctamente." : "Usuario guardado correctamente.");

        setSuccess("");
        if (isEdicion) {
          setIsEditing(false);
        } else {
          onClose();
        }
        onSave(savedUser);
      } catch (error) {
        console.error("Error al guardar usuario:", error);
        setErrors({
          general: error.response?.data?.error || "Error al guardar el usuario",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const getNombreCompleto = (usuario) => {
    return `${usuario.nombre || ""} ${usuario.apellidoPaterno || ""} ${usuario.apellidoMaterno || ""
      }`.trim();
  };

  const getTitle = () => {
    if (readOnly && !isEditing) return "Consulta de Usuario";
    if (readOnly && isEditing) return "Editar Usuario";
    if (initialData && Object.keys(initialData).length > 0) return "Editar Usuario";
    return "Nuevo Usuario";
  };

  const buttons = readOnly && isEditing ? (
    <>
      <button className="cancel-btn" onClick={handleCancelEdit}>
        Cancelar Edición
      </button>
      <button
        className="save-btn"
        onClick={handleSave}
        disabled={loading}
      >
        {loading ? "Guardando..." : "Guardar"}
      </button>
    </>
  ) : (
    <>
      <button className="cancel-btn" onClick={onClose}>
        Cancelar
      </button>
      <button
        className="save-btn"
        onClick={handleSave}
        disabled={loading}
      >
        {loading ? "Guardando..." : "Guardar"}
      </button>
    </>
  );

  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <div className="modal-header">
          <span>
            {getTitle()}
          </span>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>
        {success && (
          <div
            style={{
              color: "green",
              textAlign: "center",
              margin: "10px 0",
              fontWeight: 600,
            }}
          >
            {success}
          </div>
        )}
        {errors.general && (
          <div
            style={{
              color: "red",
              textAlign: "center",
              margin: "10px 0",
              fontWeight: 600,
            }}
          >
            {errors.general}
          </div>
        )}
        <div className="modal-tabs">
          <button
            className={tab === 0 ? "active" : ""}
            onClick={() => setTab(0)}
          >
            Datos de Usuario
          </button>
          <button
            className={tab === 2 ? "active" : ""}
            onClick={() => setTab(2)}
          >
            Equipo de trabajo
          </button>
        </div>
        <div className="modal-content">
          {tab === 0 && (
            <div className="form-grid">
              <div>
                <label htmlFor="tipoDocumento">Tipo documento</label>
                <select
                  name="tipoDocumento"
                  value={form.tipoDocumento}
                  onChange={handleChange}
                  disabled={readOnly && !isEditing}
                >
                  <option value="">Seleccionar</option>
                  {tiposDocumentos.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.description}
                    </option>
                  ))}
                </select>
                {errors.tipoDocumento && (
                  <span className="error">{errors.tipoDocumento}</span>
                )}
              </div>
              <div>
                <label htmlFor="numero">Número</label>
                <div className="input-with-button">
                  <input
                    name="numero"
                    value={form.numero}
                    onChange={handleNumericChange}
                    disabled={readOnly && !isEditing}
                    placeholder="Solo números"
                  />
                  {form.tipoDocumento == "1" && (
                    <button
                      type="button"
                      className="search-btn-documento"
                      title="Buscar cliente"
                      onClick={buscarCliente}
                    >
                      <Search size={20} color="#555" />
                    </button>
                  )}
                </div>
                {errors.numero && (
                  <span className="error">{errors.numero}</span>
                )}
              </div>
              <div>
                <label htmlFor="perfil">Perfil</label>
                <select
                  name="perfil"
                  value={form.perfil}
                  onChange={handlePerfilChange}
                  disabled={readOnly && !isEditing}
                >
                  <option value="">Seleccionar</option>
                  {perfiles.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre}
                    </option>
                  ))}
                </select>
                {errors.perfil && (
                  <span className="error">{errors.perfil}</span>
                )}
              </div>
              <div>
                <label htmlFor="nombre">Nombres</label>
                <input
                  name="nombre"
                  value={form.nombre}
                  onChange={handleChange}
                  disabled={readOnly && !isEditing}
                />
                {errors.nombre && (
                  <span className="error">{errors.nombre}</span>
                )}
              </div>
              <div>
                <label htmlFor="apellidoPaterno">Apellido paterno</label>
                <input
                  name="apellidoPaterno"
                  value={form.apellidoPaterno}
                  onChange={handleChange}
                  disabled={readOnly && !isEditing}
                />
              </div>
              <div>
                <label htmlFor="apellidoMaterno">Apellido materno</label>
                <input
                  name="apellidoMaterno"
                  value={form.apellidoMaterno}
                  onChange={handleChange}
                  disabled={readOnly && !isEditing}
                />
              </div>
              <div>
                <label htmlFor="celular">Número de celular</label>
                <input
                  name="celular"
                  value={form.celular}
                  onChange={handleNumericChange}
                  disabled={readOnly && !isEditing}
                  placeholder="Solo números"
                />
              </div>
              <div>
                <label htmlFor="email">Correo electrónico</label>
                <input
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  disabled={readOnly && !isEditing}
                />
                {errors.email && <span className="error">{errors.email}</span>}
              </div>
              <div>
                <label htmlFor="fechaNacimiento">Fecha de Nacimiento</label>
                <input
                  type="date"
                  name="fechaNacimiento"
                  value={form.fechaNacimiento}
                  onChange={handleChange}
                  disabled={readOnly && !isEditing}
                />
              </div>
              <div>
                <label htmlFor="usuario">Código Usuario</label>
                <input
                  name="usuario"
                  value={form.usuario}
                  onChange={handleChange}
                  disabled={readOnly && !isEditing}
                />
                {errors.usuario && (
                  <span className="error">{errors.usuario}</span>
                )}
              </div>
              {readOnly && !isEditing ? (
                // En modo consulta, mostrar campos simples
                <>
                  <div>
                    <label htmlFor="departamento">Departamento</label>
                    <input
                      name="departamento"
                      value={initialData.departamentoNombre}
                      onChange={handleChange}
                      disabled={readOnly && !isEditing}
                    />
                  </div>
                  <div>
                    <label htmlFor="provincia">Provincia</label>
                    <input
                      name="provincia"
                      value={initialData.provinciaNombre}
                      onChange={handleChange}
                      disabled={readOnly && !isEditing}
                    />
                  </div>
                  <div>
                    <label htmlFor="distrito">Distrito</label>
                    <input
                      name="distrito"
                      value={initialData.distritoNombre}
                      onChange={handleChange}
                      disabled={readOnly && !isEditing}
                    />
                  </div>
                </>
              ) : (
                // En modo creación, mostrar dropdowns dinámicos
                <div style={{ gridColumn: "1 / -1" }}>
                  <label htmlFor="ubicacion">Ubicación</label>
                  <LocationDropdowns
                    selectedDepartment={locationData.department}
                    selectedProvince={locationData.province}
                    selectedDistrict={locationData.district}
                    selectedZone={locationData.zone}
                    onDepartmentChange={handleDepartmentChange}
                    onProvinceChange={handleProvinceChange}
                    onDistrictChange={handleDistrictChange}
                    onZoneChange={handleZoneChange}
                    showZones={false}
                    className="location-dropdowns"
                    token={token}
                  />
                </div>
              )}
              <div style={{ gridColumn: "1 / -1" }}>
                <label htmlFor="direccion">Dirección</label>
                <textarea
                  name="direccion"
                  value={form.direccion}
                  onChange={handleChange}
                  disabled={readOnly}
                  placeholder="Ingrese la dirección completa"
                  rows={4} // Puedes ajustar el número de filas visibles
                  style={{ resize: "vertical" }} // Permite redimensionar verticalmente
                />
              </div>
            </div>
          )}
          {tab === 2 && (
            <div className="form-grid">
              <div>
                <label htmlFor="id_region">Región</label>
                <select
                  name="id_region"
                  value={form.id_region}
                  onChange={handleChange}
                  disabled={(readOnly && !isEditing) || loadingRegiones}
                >
                  <option value="">Seleccionar región</option>
                  {loadingRegiones ? (
                    <option disabled>Cargando regiones...</option>
                  ) : (
                    regionesDisponibles.map((region) => (
                      <option key={region.id} value={region.id}>
                        {region.description}
                      </option>
                    ))
                  )}
                </select>
                {errors.region && (
                  <span className="error">{errors.region}</span>
                )}
              </div>

              {/* Campo Jefe - solo visible para perfiles que lo necesitan */}
              {(tipoSeleccionado?.nombre === "Supervisor" ||
                tipoSeleccionado?.nombre === "Vendedor" ||
                tipoSeleccionado?.nombre === "Callcenter" ||
                tipoSeleccionado?.nombre === "Asesor") && (
                  <div>
                    <label htmlFor="jefe">Jefe</label>
                    <select
                      name="jefe"
                      value={form.jefe}
                      onChange={handleChange}
                      disabled={(readOnly && !isEditing) || loadingJefes}
                    >
                      <option value="">Seleccionar jefe</option>
                      {loadingJefes ? (
                        <option disabled>Cargando jefes...</option>
                      ) : (
                        jefesDisponibles.map((jefe) => (
                          <option key={jefe.id} value={jefe.id}>
                            {getNombreCompleto(jefe)} - {jefe.region}
                          </option>
                        ))
                      )}
                    </select>
                    {errors.jefe && <span className="error">{errors.jefe}</span>}
                  </div>
                )}

              {/* Campo Supervisor - solo visible para perfiles que lo necesitan */}
              {(tipoSeleccionado?.nombre === "Vendedor" ||
                tipoSeleccionado?.nombre === "Callcenter" ||
                tipoSeleccionado?.nombre === "Asesor") && (
                  <div>
                    <label htmlFor="supervisor">Supervisor</label>
                    <select
                      name="supervisor"
                      value={form.supervisor}
                      onChange={handleChange}
                      disabled={(readOnly && !isEditing) || loadingSupervisores}
                    >
                      <option value="">Seleccionar supervisor</option>
                      {loadingSupervisores ? (
                        <option disabled>Cargando supervisores...</option>
                      ) : (
                        supervisoresDisponibles.map((supervisor) => (
                          <option
                            key={supervisor.id}
                            value={supervisor.id}
                          >
                            {getNombreCompleto(supervisor)} - {supervisor.region}
                          </option>
                        ))
                      )}
                    </select>
                    {errors.supervisor && (
                      <span className="error">{errors.supervisor}</span>
                    )}
                  </div>
                )}

              {perfilesEquipoTrabajo.includes(tipoSeleccionado?.nombre) && (
                <div
                  className="full-width"
                  style={{
                    background:
                      readOnly && isEditing
                        ? "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)"
                        : "linear-gradient(135deg, #fff5f5 0%, #fed7d7 100%)",
                    border:
                      readOnly && isEditing
                        ? "1px solid #7dd3fc"
                        : "1px solid #feb2b2",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    marginTop: "16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      background: readOnly && isEditing ? "#0ea5e9" : "#e53e3e",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <span
                      style={{
                        color: "white",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      {readOnly && isEditing ? "ℹ" : "!"}
                    </span>
                  </div>
                  <div
                    style={{
                      color: readOnly && isEditing ? "#0369a1" : "#c53030",
                      fontSize: "14px",
                      fontWeight: "500",
                      lineHeight: "1.4",
                    }}
                  >
                    <strong>
                      {readOnly && isEditing
                        ? "Información opcional:"
                        : "Información requerida:"}
                    </strong>
                    {readOnly && isEditing
                      ? " Puede asignar un jefe y supervisor para este usuario."
                      : " Los campos de equipo de trabajo son obligatorios para el perfil seleccionado."}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer">
          {readOnly && !isEditing ? (
            <>
              <button className="cancel-btn" onClick={onClose}>
                Cancelar
              </button>
              <Permiso permiso="edit_user">
                <button className="edit-btn" onClick={handleEditMode}>
                  Editar
                </button>
              </Permiso>
            </>
          ) : (buttons)
        }
        </div>
      </div>
    </div>
  );
}

export default NewUserModal;
